'use client';

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './Dialog';

export type ErrorDialogPayload = {
  title?: string;
  message: string;

  // opcional: conteúdo extra (stack, requestId, etc.)
  details?: string;

  // labels
  primaryLabel?: string;  // ex: "Tentar novamente"
  secondaryLabel?: string; // ex: "Fechar"

  // eventos
  onPrimary?: () => void | Promise<void>;
  onSecondary?: () => void | Promise<void>;
  onClose?: () => void;

  // comportamentos
  dismissible?: boolean; // default true
};

type Ctx = {
  showError: (p: ErrorDialogPayload) => void;
  hideError: () => void;
};

const ErrorDialogContext = createContext<Ctx | null>(null);

/** ✅ Função global (opcional) pra chamar sem hook */
let globalShowError: ((p: ErrorDialogPayload) => void) | null = null;
export const showError = (p: ErrorDialogPayload) => globalShowError?.(p);

export function useErrorDialog() {
  const ctx = useContext(ErrorDialogContext);
  if (!ctx) throw new Error('useErrorDialog deve ser usado dentro de <ErrorDialogProvider />');
  return ctx;
}

export function ErrorDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<ErrorDialogPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const hideError = () => {
    setOpen(false);
    payload?.onClose?.();
    // delay pequeno pra animação fechar antes de limpar
    setTimeout(() => {
      setPayload(null);
      setBusy(false);
      setShowDetails(false);
    }, 150);
  };

  const showErrorInternal = (p: ErrorDialogPayload) => {
    setPayload({
      title: p.title ?? 'Ocorreu um erro',
      primaryLabel: p.primaryLabel ?? (p.onPrimary ? 'Tentar novamente' : undefined),
      secondaryLabel: p.secondaryLabel ?? 'Fechar',
      dismissible: p.dismissible ?? true,
      ...p,
    });
    setOpen(true);
  };

  // expõe função global
  useEffect(() => {
    globalShowError = showErrorInternal;
    return () => {
      globalShowError = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      showError: showErrorInternal,
      hideError,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload, open]
  );

  const run = async (fn?: () => void | Promise<void>) => {
    if (!fn) return;
    try {
      setBusy(true);
      await fn();
      // se o callback não fechou por conta própria, fechamos
      hideError();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ErrorDialogContext.Provider value={value}>
      {children}

      <Dialog open={open} onOpenChange={(v) => (payload?.dismissible ? setOpen(v) : setOpen(true))}>
        <DialogContent
          onClose={() => (payload?.dismissible ? hideError() : undefined)}
          className="lb-dialog lb-alert ornate-card relative w-[min(460px,92vw)] text-white"
        >
          {/* Header */}
          <DialogHeader>
            <DialogTitle className="lb-dialog-title">
              {payload?.title ?? 'Ocorreu um erro'}
            </DialogTitle>

            <DialogDescription className="lb-alert-msg">
              {payload?.message ?? ''}
            </DialogDescription>
          </DialogHeader>

          {/* Details (opcional) */}
          {payload?.details && (
            <div className="mt-1">
              <button
                type="button"
                className="lb-alert-link"
                onClick={() => setShowDetails((s) => !s)}
              >
                {showDetails ? 'Ocultar detalhes' : 'Ver detalhes'}
              </button>

              {showDetails && (
                <pre className="mt-2 max-h-44 overflow-auto rounded-xl border border-white/10 bg-black/40 p-3 text-xs text-white/70">
{payload.details}
                </pre>
              )}
            </div>
          )}

          {/* Actions */}
          <DialogFooter className="mt-2">
            <button
              type="button"
              className={payload?.onPrimary ? 'btn btn-dark' : 'btn btn-gold'}
              disabled={busy}
              onClick={() => run(payload?.onSecondary ?? (() => hideError()))}
            >
              {payload?.secondaryLabel ?? 'Fechar'}
            </button>

            {payload?.onPrimary && (
              <button
                type="button"
                className="btn btn-gold"
                disabled={busy}
                onClick={() => run(payload.onPrimary)}
              >
                {busy ? 'Aguarde...' : (payload.primaryLabel ?? 'Tentar novamente')}
              </button>
            )}
          </DialogFooter>

        </DialogContent>
      </Dialog>
    </ErrorDialogContext.Provider>
  );
}
